import React, { useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Popper, Tooltip } from '@material-ui/core';
import { Card, CardContent } from '@mesh.ai/rabbit';
import { CompanyResponseHover } from 'screens/Engagement/CommonComps';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import LoadingComponent from 'screens/common/sidepanel/LoadingComponent';
import styled from 'styled-components';
import { CenterItemFlexContainer, StyledBox, StyledPaper } from 'styles/common';

import { ENGAGEMENT_DRIVERS_TAG_SUB_TYPES } from 'constants/ProjectConstants';
import { AnonymousIcon } from 'constants/icons';
import theme from 'constants/theme';

import DateComp from 'uiToolkit/DateComp';
import Score from 'uiToolkit/Score';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

const Dash = styled.div`
  width: 48px;
  height: 8px;
  border-radius: 4px;
  background-color: ${theme.palette.background.lightGrey3};
`;

export const EmptyBar = styled.div`
  width: 1.75rem;
  height: 0.5rem;
  border-radius: 0.5rem;
  margin-bottom: ${(props) => props.selected && '0.375rem'};
  background-color: ${(props) =>
    props.selected ? props.theme.palette.primary.main : props.theme.palette.text.secondaryLight};
`;

const StyledTag = styled.div`
  display: inline-block;
  text-align: center;
  padding-left: 10px;
  padding-right: 10px;
  background-color: ${(props) => props.bgColor};
  border-radius: 1rem;
  color: white;
  // cursor: pointer;
`;

function cutString(s, n) {
  const cut = s.indexOf(' ', n);
  if (cut === -1) return s;
  return s.substring(0, cut);
}

const CompanyResponse = ({ overallRating, options }) => {
  const ref = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleMouseOver = () => {
    if (overallRating !== null && overallRating !== undefined && options?.length > 0) setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (overallRating !== null && overallRating !== undefined && options?.length > 0) setIsOpen(false);
  };

  const fixedLengthNumber = (number = 0) => Number(number.toFixed(2));

  return (
    <>
      <Box ref={ref} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
        <StyledBox display="flex" justifyContent="center" alignItems="center">
          {overallRating !== null && overallRating !== undefined ? (
            <TextBodyMedium customColor="text.g2">{fixedLengthNumber(overallRating)}</TextBodyMedium>
          ) : (
            <EmptyBar />
          )}
        </StyledBox>
      </Box>
      <Popper open={isOpen} anchorEl={ref.current} transition style={{ zIndex: 5 }}>
        <StyledPaper>
          <CompanyResponseHover data={options} />
        </StyledPaper>
      </Popper>
    </>
  );
};

const NoDataDash = () => (
  <CenterItemFlexContainer>
    <Dash />
  </CenterItemFlexContainer>
);

const SurveyMetaInfo = ({ name, isAnonymous, endDateFormatted }) => (
  <Flex alignItems="center" justifyContent="space-between" marginBottom="24px">
    <TextHeader2 bold>{name}</TextHeader2>
    <Flex alignItems="center">
      {isAnonymous && (
        <>
          <AnonymousIcon color={theme.palette.grey.main} size="tiny" rightGutter={2} />
          <TextBodyMedium marginRight="16px" color="text.g2">
            You are anonymous
          </TextBodyMedium>
        </>
      )}
      <DateComp
        editable={false}
        startDateTime={endDateFormatted.timeStamp}
        color={endDateFormatted.color}
        isCrossed={endDateFormatted.isCrossed}
        tooltip={undefined}
        endDateTime={undefined}
      />
    </Flex>
  </Flex>
);

const Tag = React.memo(({ tagSubType }) => {
  const { tagId, displayName, bgColor } = ENGAGEMENT_DRIVERS_TAG_SUB_TYPES[tagSubType] ?? {};
  return <StyledTag bgColor={bgColor}>{displayName}</StyledTag>;
});

/*  only following ids are handled. displayName is not handled on UI, whatever backend sends will work out.
    { id: 1, displayName: "Question Name"}
    { id: 2, displayName: "Comment"}
    { id: 3, displayName: "Your Response"}
    { id: 4, displayName: "Company Response"}
  */

const HANDLED_IDS_META = {
  1: {
    id: 1,
    width: '60%',
  },
  2: {
    id: 2,
    width: '25%',
  },
  3: {
    id: 3,
    width: 'auto',
  },
  4: {
    id: 4,
    width: '15rem',
  },
};
const handledIds = [1, 2, 3, 4];
const tagInfoColumnId = 5;

const components = {
  // 1 = Question Name
  1: ({ value, isRequired, tagInfo = {} }) => {
    const { tagSubType } = tagInfo;

    return (
      <TextBodyMedium>
        {value}
        {isRequired ? '*' : ''}
        &nbsp; &nbsp; &nbsp; &nbsp;
        {Boolean(tagSubType) && <Tag tagSubType={tagSubType} />}
      </TextBodyMedium>
    );
  },
  // 2 = Comment
  2: ({ value }) => (
    <Box>
      {value ? (
        <Tooltip title={value}>
          <TextBodyMedium>
            {cutString(value, 50)}
            {cutString(value, 50).length < value.length ? '...' : ''}
          </TextBodyMedium>
        </Tooltip>
      ) : (
        <NoDataDash />
      )}
    </Box>
  ),
  // 3 = Your Response
  3: ({ value }) => (
    <Flex
      alignItems="center"
      sx={{
        // target 1st div inside Flex
        '> div': {
          alignItems: 'center',
        },
      }}
    >
      {value ? <Score {...value} /> : <NoDataDash />}
    </Flex>
  ),
  // 4 = Company Response
  4: ({ overallRating, options = [] }) => <CompanyResponse overallRating={overallRating} options={options} />,
};

const getFieldName = (id) => {
  const fieldMap = {
    1: 'questionName',
    2: 'comment',
    3: 'yourResponse',
    4: 'companyResponse',
  };
  return fieldMap[id] || `field_${id}`; // Fallback for unknown columns
};

const getCellRenderer = (id) => {
  const rendererMap = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
  };
  return rendererMap[id] || null;
};

const getValueGetter = (id) => (params) => {
  const cellData = params.data?.[id];
  if (id === 1) {
    return { ...cellData, tagInfo: params.data[tagInfoColumnId]?.value } ?? null;
  }
  return cellData ?? null;
};

const getRestOfColumnProps = (id) => {
  const restColProps = {
    1: {
      flex: 2,
      minWidth: 300,
    },
    2: {
      flex: 1,
    },
    3: {
      minWidth: 350,
      flex: 1,
    },
    4: {
      flex: 1,
    },
  };
  return restColProps[id] || {};
};

const EngagementSurveyResultsTable = ({ resultData, isLoading, isError }) => {
  const { isAnonymous, endDateFormatted, name, columns, values } = resultData ?? {};

  const filteredColumns = useMemo(() => {
    if (!columns) return [];
    return columns.filter(({ id }) => handledIds.includes(id));
  }, [columns]);

  const columnDefs = useMemo(() => {
    if (!filteredColumns) return [];

    return filteredColumns.map(({ id, displayName }) => {
      const columnDef = {
        headerName: displayName,
        field: getFieldName(id),
        valueGetter: getValueGetter(id),
        cellRenderer: ({ value }) => components[getCellRenderer(id)](value),
        ...getRestOfColumnProps(id),
      };
      return columnDef;
    });
  }, [filteredColumns]);

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: false,
      filter: false,
      editable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      headerComponent: CustomHeaderCell,
      minWidth: 200,
      flex: 1,
      cellStyle: { display: 'flex', alignItems: 'center', overflow: 'hidden', wordBreak: 'break-word' },
      headerComponentParams: {
        enableMenu: false,
        enableSorting: false,
      },
      autoHeight: true,
      wrapText: true,
    }),
    []
  );

  if (isLoading) {
    return <LoadingComponent />;
  }
  if (isError) {
    return <TextBodyMedium>Network error. Please refresh to try again.</TextBodyMedium>;
  }
  return (
    <Card>
      <CardContent>
        <SurveyMetaInfo endDateFormatted={endDateFormatted} isAnonymous={isAnonymous} name={name} />

        <AgGridTable
          defaultColDef={defaultColDef}
          rowData={values}
          columnDefs={columnDefs}
          animateRows={false}
          domLayout="autoHeight"
          headerHeight={36}
          rowHeight={100}
          containerAutoHeight
          pagination={false}
          rowStyle={{
            cursor: 'default',
          }}
          kebabMenuIsRowSpecific={false}
          suppressContextMenu
          showSidebarToggle={false}
        />
      </CardContent>
    </Card>
  );
};

export { EngagementSurveyResultsTable };
